import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { networksApi } from '@/api/networksApi'
import { useAuth } from '@/providers/AuthProvider'
import {
  ACTIVITY_EVENTS,
  getLastActivityAt,
  isActivityStorageKey,
  markActivity,
  setSessionEndedReason,
  startIdleClock,
} from '@/lib/idleActivity'
import { evaluateIdle, resolveIdleMs, type IdleState } from '@/lib/idleClock'
import { clearLastLocation, recordLastLocation } from '@/lib/lastLocation'
import { surfaceOf } from '@/lib/viewMode'

/**
 * How often the clock is re-evaluated.
 *
 * A poll rather than a single `setTimeout` for the whole period, because a
 * timer scheduled 30 minutes out does not fire while the machine sleeps — it
 * fires late, after the user is already back, and the session would have
 * survived an unattended night. Re-deriving from a stored timestamp on a short
 * interval means a slept-through window is caught on the first tick after wake.
 *
 * Fifteen seconds is well inside the 2-minute warning, so the countdown never
 * visibly stalls, and it is 4 evaluations a minute — nothing next to what the
 * app already polls.
 */
const POLL_MS = 15_000

interface IdleSession {
  state: IdleState
  msRemaining: number
  /** Reset the clock and dismiss the warning. The ONLY thing that does. */
  staySignedIn: () => void
  /** End the session now, deliberately: no location is remembered. */
  signOutNow: () => void
}

/**
 * Drives the idle timeout for whichever surface mounts it.
 *
 * Lives in a hook, not in `AuthProvider`, for a concrete reason: the provider
 * sits ABOVE the router in both entries, so it cannot call `useNavigate` and
 * cannot know which route the user is on — and this feature needs both.
 *
 * Deliberately NOT unit-tested: `vitest.config.ts` is node-environment and
 * collects only `src/**\/*.test.ts`, so a hook test here would silently never
 * run. Everything with a decision in it lives in `idleClock.ts` and
 * `idleActivity.ts`, which are tested; what is left here is wiring.
 */
export function useIdleSession(): IdleSession {
  const { isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [status, setStatus] = useState<{ state: IdleState; msRemaining: number }>({
    state: 'active',
    msRemaining: 0,
  })

  // The instance-wide setting. Shares the query key the top bar already uses,
  // so reading it here costs no extra request. A failure falls through to the
  // default rather than disabling the timeout.
  const { data: network } = useQuery({
    queryKey: ['instance-network'],
    queryFn: () => networksApi.current(),
    enabled: isAuthenticated,
  })
  const idleMs = resolveIdleMs(network?.idleTimeoutMinutes)

  // Held in a ref so the poll and the listeners read the live route without
  // being torn down and rebuilt on every navigation.
  const locationRef = useRef(location)
  locationRef.current = location

  // Guards the end-of-session path against running twice — the poll and a
  // visibility change can both observe `expired` in the same tick.
  const endingRef = useRef(false)

  const endSession = useCallback(async () => {
    if (endingRef.current) return
    endingRef.current = true

    // Order matters: both calls need the bearer token, so they run BEFORE
    // logout() clears it.
    setSessionEndedReason('inactivity')
    await recordLastLocation({
      pathname: locationRef.current.pathname,
      search: locationRef.current.search,
      tenantId: localStorage.getItem('cgt_selected_tenant'),
      // From window.location, not the router: under the mobile basename the
      // router reports `/tasks` while the real path is `/m/tasks`, and the
      // surface is exactly what distinguishes the two route tables.
      surface: surfaceOf(window.location.pathname),
    })

    logout()
    navigate('/login', { replace: true })
  }, [logout, navigate])

  const staySignedIn = useCallback(() => {
    // Forced: the clock has by definition seen no activity for a while, so the
    // throttle would otherwise swallow this exact press.
    startIdleClock()
    setStatus({ state: 'active', msRemaining: idleMs })
  }, [idleMs])

  const signOutNow = useCallback(() => {
    endingRef.current = true
    // A deliberate sign-out forgets where you were, and must not leave the
    // stored route (which can contain a patient identifier) behind.
    void clearLastLocation()
    logout()
    navigate('/login', { replace: true })
  }, [logout, navigate])

  // Read by the activity listener, which must not be rebuilt on every tick.
  const statusRef = useRef(status)
  statusRef.current = status

  // Observe activity. DOM events only: background traffic — the notifications
  // socket, the polling hooks, the token renewal in authFetch — must never
  // make a walked-away session look busy.
  useEffect(() => {
    if (!isAuthenticated) return

    const onActivity = () => {
      // Frozen while the warning is up. Without this, the mouse movement that
      // reaches for "Stay signed in" would silently cancel the timeout, and
      // dismissing the dialog by clicking away would count as staying — both
      // of which would make the warning meaningless.
      if (statusRef.current.state === 'warning') return
      markActivity()
    }

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true })
    }
    return () => {
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, onActivity)
    }
  }, [isAuthenticated])

  // Evaluate on a tick, and again whenever the tab comes back. Returning to a
  // tab re-checks but never resets: a machine that slept past the threshold
  // must end the session, not be granted a fresh window.
  useEffect(() => {
    if (!isAuthenticated) {
      endingRef.current = false
      return
    }

    const tick = () => {
      const next = evaluateIdle({
        now: Date.now(),
        lastActivityAt: getLastActivityAt(),
        idleMs,
      })
      setStatus(next)
      if (next.state === 'expired') void endSession()
    }

    tick()
    const timer = window.setInterval(tick, POLL_MS)
    // A `storage` event means another tab on this machine saw activity, or the
    // clock was cleared. Several tabs are one session, so they re-evaluate
    // together instead of each running its own countdown.
    const onStorage = (e: StorageEvent) => {
      if (isActivityStorageKey(e.key)) tick()
    }

    window.addEventListener('visibilitychange', tick)
    window.addEventListener('focus', tick)
    window.addEventListener('storage', onStorage)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('visibilitychange', tick)
      window.removeEventListener('focus', tick)
      window.removeEventListener('storage', onStorage)
    }
  }, [isAuthenticated, idleMs, endSession])

  return { state: status.state, msRemaining: status.msRemaining, staySignedIn, signOutNow }
}
